import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Your password is set — sign in with it now");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl">
        <div className="kicker mb-2 flex items-center gap-1.5">
          <ShieldCheck className="h-3 w-3" /> ServeCOR
        </div>
        <h1 className="mb-1 font-display text-xl font-semibold text-foreground">
          {done ? "Your password is set" : "Set a new password"}
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {done
            ? "Sign in with your new password."
            : token
              ? "Choose something only you know — at least 8 characters."
              : "This link is missing its token. Ask for a fresh reset from the sign-in page."}
        </p>

        {done ? (
          <Button onClick={() => navigate("/login")} data-testid="reset-go-to-login"
                  className="w-full rounded-full bg-primary text-primary-foreground">
            Go to sign-in
          </Button>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">New password</Label>
              <Input type="password" value={password} data-testid="reset-password-input"
                     onChange={(e) => setPassword(e.target.value)} className="bg-muted/40" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Type it again</Label>
              <Input type="password" value={again} data-testid="reset-password-confirm"
                     onChange={(e) => setAgain(e.target.value)} className="bg-muted/40" />
            </div>
            {again && password !== again && (
              <p className="text-[11.5px] text-red-500" data-testid="reset-mismatch">
                Those two do not match.
              </p>
            )}
            <Button type="submit" data-testid="reset-submit"
                    disabled={busy || !token || password.length < 8 || password !== again}
                    className="w-full rounded-full bg-primary text-primary-foreground">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <KeyRound className="mr-2 h-4 w-4" />} Set my password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
